import { RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useModalFocus } from "../hooks/useModalFocus";
import type { QualityTier } from "../types";

interface SettingsDialogProps {
  open: boolean;
  reducedMotion: boolean;
  quality: QualityTier;
  aiMode: "online" | "local";
  onReducedMotionChange: (enabled: boolean) => void;
  onQualityChange: (quality: QualityTier) => void;
  onAiModeChange: (mode: "online" | "local") => void;
  onReset: () => void;
  onClose: () => void;
}

// 设置面板集中管理可访问性、性能与 AI 数据模式。
export function SettingsDialog(props: SettingsDialogProps) {
  const { dialogRef, initialFocusRef, requestClose } = useModalFocus(props.open, props.onClose);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const resetTriggerRef = useRef<HTMLButtonElement>(null);
  const cancelResetRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!props.open) setConfirmingReset(false);
  }, [props.open]);

  useEffect(() => {
    if (!confirmingReset) return;
    cancelResetRef.current?.focus({ preventScroll: true });
  }, [confirmingReset]);

  function cancelReset() {
    setConfirmingReset(false);
    window.requestAnimationFrame(() => resetTriggerRef.current?.focus({ preventScroll: true }));
  }

  if (!props.open) return null;

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={requestClose}>
      <section ref={dialogRef} className="settings-dialog" role="dialog" aria-modal="true" aria-label="体验设置" tabIndex={-1} onMouseDown={(event) => event.stopPropagation()}>
        <div className="settings-dialog__heading">
          <div><span className="section-kicker">体验设置</span><h2>观察方式</h2></div>
          <button ref={initialFocusRef} className="icon-button" type="button" onClick={requestClose} title="关闭设置" aria-label="关闭设置"><X size={20} /></button>
        </div>
        <label className="toggle-row">
          <span><strong>减少动态</strong><small>弱化镜头、漂浮和粒子运动</small></span>
          <input type="checkbox" checked={props.reducedMotion} onChange={(event) => props.onReducedMotionChange(event.target.checked)} />
        </label>
        <div className="settings-group">
          <strong>画质</strong>
          <div className="segmented-control">
            {(["high", "balanced", "low"] as QualityTier[]).map((value) => (
              <button type="button" key={value} aria-pressed={props.quality === value} className={props.quality === value ? "is-active" : ""} onClick={() => props.onQualityChange(value)}>
                {value === "high" ? "精细" : value === "balanced" ? "均衡" : "省电"}
              </button>
            ))}
          </div>
        </div>
        <div className="settings-group">
          <strong>学习助手</strong>
          <div className="segmented-control">
            <button type="button" aria-pressed={props.aiMode === "online"} className={props.aiMode === "online" ? "is-active" : ""} onClick={() => props.onAiModeChange("online")}>在线 AI</button>
            <button type="button" aria-pressed={props.aiMode === "local"} className={props.aiMode === "local" ? "is-active" : ""} onClick={() => props.onAiModeChange("local")}>本地提示</button>
          </div>
        </div>
        {confirmingReset ? (
          <div className="reset-confirmation" role="alert" aria-label="确认清除学习记录">
            <div>
              <strong>确认清除全部本机记录？</strong>
              <p>章节进度、得分和对话记录将被删除，且无法撤销。</p>
            </div>
            <div className="reset-confirmation__actions">
              <button ref={cancelResetRef} className="command-button command-button--secondary" type="button" onClick={cancelReset}>取消</button>
              <button className="danger-command danger-command--confirm" type="button" onClick={props.onReset}><RotateCcw size={17} /> 确认清除</button>
            </div>
          </div>
        ) : (
          <button ref={resetTriggerRef} className="danger-command" type="button" onClick={() => setConfirmingReset(true)}><RotateCcw size={17} /> 清除本机学习记录</button>
        )}
      </section>
    </div>
  );
}
